import * as signalR from "@microsoft/signalr"
import { useAuthStore } from "stores/auth"
import { useProcessMonitorStore } from "stores/process-monitor"
import { newArchAxiosInstance } from "utils/request"
import { newArchBaseUrl } from "utils/constants"
import {
  getTransactionKey,
  isActiveTransaction,
  isTerminalTransaction,
  normalizeTransactionType,
  TrackTransactionInput,
  TransactionProgress,
} from "./types"
import {
  getProgressUpdateTime,
  shouldApplyRecoverySnapshot,
} from "./recovery"

type ConnectionState = {
  connection: signalR.HubConnection
  subscriptions: Set<string>
  startPromise?: Promise<void>
  retryTimer?: number
}

let connectionState: ConnectionState | undefined
let recoveryTimer: number | undefined
const liveUpdateVersions = new Map<string, number>()
const RECOVERY_POLL_INTERVAL_MS = 10000

const normalizeProgress = (
  progress: TransactionProgress,
  fallbackType?: string,
  observedAt?: string
): TransactionProgress => {
  const type = normalizeTransactionType(
    progress.type || fallbackType || "default"
  )

  return {
    ...progress,
    // Core intentionally sends null ids for keyless uploads. Their unique type
    // is the stable identifier while the organisation-wide process is active.
    id: (progress.id || type).toString(),
    type,
    updatedBy: progress.initiatedBy || progress.updatedBy,
    updatedAt: getProgressUpdateTime({
      serverUpdatedAt: progress.updatedAt,
      observedAt,
      initiatedAt: progress.initiatedAt,
    }),
    startedAt: progress.startedAt || progress.initiatedAt,
  }
}

export const applyLiveTransactionProgress = (
  payload: TransactionProgress
) => {
  const progress = normalizeProgress(
    payload,
    undefined,
    new Date().toISOString()
  )
  const key = getTransactionKey(progress.type, progress.id)
  liveUpdateVersions.set(key, (liveUpdateVersions.get(key) || 0) + 1)
  useProcessMonitorStore.getState().upsertProcess(progress)
  syncRecoveryPolling()
}

const hasActiveProcesses = () =>
  Object.values(useProcessMonitorStore.getState().processes).some(
    (process) => isActiveTransaction(process.status)
  )

const isLiveConnectionConnected = () =>
  connectionState?.connection.state === signalR.HubConnectionState.Connected

const stopRecoveryPolling = () => {
  if (!recoveryTimer) return
  window.clearInterval(recoveryTimer)
  recoveryTimer = undefined
}

const shouldPollForRecovery = () =>
  hasActiveProcesses() && !isLiveConnectionConnected()

const syncRecoveryPolling = () => {
  if (shouldPollForRecovery()) {
    startRecoveryPolling()
    return
  }

  stopRecoveryPolling()
}

const startRecoveryPolling = () => {
  if (recoveryTimer) return
  recoveryTimer = window.setInterval(() => {
    if (!shouldPollForRecovery()) {
      stopRecoveryPolling()
      return
    }

    void recoverLatestTransactions().finally(syncRecoveryPolling)
  }, RECOVERY_POLL_INTERVAL_MS)
}

const markTransactionQueued = (type: string, id: string | number) => {
  const key = getTransactionKey(type, id)
  const process = useProcessMonitorStore.getState().processes[key]
  if (process?.status !== "connecting") return

  useProcessMonitorStore.getState().upsertProcess({
    ...process,
    status: "queued",
    message: "Waiting for processing to start",
    updatedAt: new Date().toISOString(),
  })
}

const restoreSubscription = async (state: ConnectionState) => {
  await state.connection.invoke("Subscribe")
  await recoverLatestTransactions()
  syncRecoveryPolling()
}

const scheduleConnectionRetry = (state: ConnectionState) => {
  syncRecoveryPolling()
  if (state.retryTimer) return

  state.retryTimer = window.setTimeout(async () => {
    state.retryTimer = undefined
    try {
      await startConnection(state)
      await restoreSubscription(state)
    } catch {
      scheduleConnectionRetry(state)
    }
  }, 5000)
}

const getConnection = (): ConnectionState => {
  if (connectionState) return connectionState

  const connection = new signalR.HubConnectionBuilder()
    .withUrl(
      `${(newArchBaseUrl || "").replace(/\/$/, "")}/hubs/transaction-progress`,
      {
        accessTokenFactory: () => useAuthStore.getState().authToken || "",
        // Connect directly so load-balanced deployments do not negotiate on
        // one Core instance and attempt the transport on another.
        transport: signalR.HttpTransportType.WebSockets,
        skipNegotiation: true,
        withCredentials: false,
      }
    )
    .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
    .configureLogging(signalR.LogLevel.Warning)
    .build()

  const state: ConnectionState = {
    connection,
    subscriptions: new Set<string>(),
  }

  connection.on(
    "TransactionProgressUpdated",
    applyLiveTransactionProgress
  )

  connection.onreconnected(async () => {
    await restoreSubscription(state).catch(() => undefined)
  })

  connection.onclose(() => scheduleConnectionRetry(state))

  connectionState = state
  return state
}

const startConnection = async (state: ConnectionState) => {
  if (state.connection.state === signalR.HubConnectionState.Connected) return
  if (
    state.connection.state === signalR.HubConnectionState.Reconnecting ||
    state.connection.state === signalR.HubConnectionState.Disconnecting
  ) {
    return
  }

  if (!state.startPromise) {
    state.startPromise = state.connection
      .start()
      .catch((error) => {
        scheduleConnectionRetry(state)
        throw error
      })
      .finally(() => {
        state.startPromise = undefined
        syncRecoveryPolling()
      })
  }

  await state.startPromise
}

export const subscribeToTransaction = async (
  type: string,
  id: string | number
) => {
  const transactionId = id.toString()
  const state = getConnection()
  state.subscriptions.add(getTransactionKey(type, transactionId))

  await startConnection(state)
  if (state.connection.state === signalR.HubConnectionState.Connected) {
    await state.connection.invoke("Subscribe")
  }
}

export const recoverLatestTransactions = async () => {
  const liveVersionsAtRequestStart = new Map(liveUpdateVersions)

  try {
    const response = await newArchAxiosInstance.get(
      "/TransactionProgress/Latest"
    )
    const progressItems = (response.data?.data || []) as TransactionProgress[]
    const latestKeys = new Set<string>()

    progressItems.forEach((item) => {
      let progress = normalizeProgress(item)
      const key = getTransactionKey(progress.type, progress.id)
      const existing = useProcessMonitorStore.getState().processes[key]

      // Core currently preserves initiatedAt when the same transaction runs
      // again and does not return updatedAt. A matching optimistic process
      // proves this snapshot belongs to the run currently open in the UI.
      if (!item.updatedAt && isActiveTransaction(existing?.status)) {
        progress = normalizeProgress(
          item,
          undefined,
          new Date().toISOString()
        )
      }

      latestKeys.add(key)

      // Core writes Redis before broadcasting over SignalR. If a live event
      // arrived while this request was in flight, this response may contain
      // the older snapshot and must not replace that event.
      if (
        !shouldApplyRecoverySnapshot(
          key,
          liveVersionsAtRequestStart,
          liveUpdateVersions
        )
      ) {
        return
      }

      useProcessMonitorStore.getState().upsertProcess(progress)
    })

    Object.entries(useProcessMonitorStore.getState().processes).forEach(
      ([key, existing]) => {
        if (latestKeys.has(key) || !isActiveTransaction(existing.status)) return

        const lastUpdatedAt = new Date(existing?.updatedAt || 0).getTime()
        const isPastStartupGracePeriod =
          Number.isFinite(lastUpdatedAt) && Date.now() - lastUpdatedAt > 60000

        if (isPastStartupGracePeriod) {
          useProcessMonitorStore.getState().upsertProcess({
            ...existing,
            status: "failed",
            message:
              "Progress is no longer available. Start the process again.",
            updatedAt: new Date().toISOString(),
          })
        }
      }
    )

    syncRecoveryPolling()

    return progressItems
  } catch {
    // The live connection remains authoritative when recovery is unavailable.
    syncRecoveryPolling()
    return []
  }
}

export const recoverTransaction = async (type: string, id: string | number) => {
  await recoverLatestTransactions()

  return useProcessMonitorStore.getState().processes[
    getTransactionKey(type, id)
  ]
}

export const beginTransactionTracking = async ({
  id,
  type,
  title,
  message,
  destinationPath,
}: TrackTransactionInput) => {
  const transactionId = id.toString()
  const normalizedType = normalizeTransactionType(type)
  const now = new Date().toISOString()

  await recoverLatestTransactions()
  const activeProcess = Object.values(
    useProcessMonitorStore.getState().processes
  ).find((process) => isActiveTransaction(process.status))

  if (activeProcess) {
    useProcessMonitorStore.getState().setOpen(true)
    throw new Error(
      `${
        activeProcess.title || "Another process"
      } is in progress. Please wait for it to finish before starting another process.`
    )
  }

  useProcessMonitorStore.getState().upsertProcess({
    id: transactionId,
    type: normalizedType,
    status: "connecting",
    title,
    message,
    progress: 0,
    updatedAt: now,
    startedAt: now,
    originPath: `${window.location.pathname}${window.location.search}`,
    destinationPath,
  })

  try {
    await subscribeToTransaction(normalizedType, transactionId)
    markTransactionQueued(normalizedType, transactionId)
  } catch {
    // Latest-progress recovery still works if WebSockets are temporarily unavailable.
  }
  syncRecoveryPolling()
}

export const isTransactionOriginCurrent = (
  type: string,
  id: string | number
) => {
  const process =
    useProcessMonitorStore.getState().processes[getTransactionKey(type, id)]
  if (!process?.originPath) return true

  return (
    process.originPath ===
    `${window.location.pathname}${window.location.search}`
  )
}

export const failTransactionTracking = (
  type: string,
  id: string | number,
  message: string
) => {
  const key = getTransactionKey(type, id)
  const existing = useProcessMonitorStore.getState().processes[key]
  if (!existing || isTerminalTransaction(existing.status)) return

  useProcessMonitorStore.getState().upsertProcess({
    ...existing,
    status: "failed",
    message,
    updatedAt: new Date().toISOString(),
  })
}

export const reconcileTransactionRequestError = async (
  type: string,
  id: string | number,
  fallbackMessage: string
) => {
  const process = await recoverTransaction(type, id)
  const hasAuthoritativeProgress =
    process?.organisationId != null || !!process?.initiatedAt

  if (hasAuthoritativeProgress) {
    return process
  }

  failTransactionTracking(type, id, fallbackMessage)
  return undefined
}

export const restoreTransactionTracking = async () => {
  const state = getConnection()
  let didRecover = false
  await startConnection(state)
    .then(async () => {
      await restoreSubscription(state)
      didRecover = true
    })
    .catch(() => undefined)
  if (!didRecover) {
    await recoverLatestTransactions()
  }
  syncRecoveryPolling()
}

export const stopTransactionTracking = async () => {
  const state = connectionState
  connectionState = undefined
  liveUpdateVersions.clear()
  stopRecoveryPolling()

  if (!state) return
  if (state.retryTimer) window.clearTimeout(state.retryTimer)
  if (state.connection.state !== signalR.HubConnectionState.Disconnected) {
    await state.connection.stop()
  }
}
